import React, { useState } from 'react';
import { Button } from './ui/button';
import { X, User, PenLine } from 'lucide-react';

// Director's Guarantee blurbs for different locations
const GUARANTEE_BLURBS = {
  blueprint: `"My family didn't just find the blueprint; we built it. Every diagnostic in this Combine is designed to bridge the gap between where your athlete is and where they need to be. We've done the work—now it's your turn to make the pivot."`,
  performance: `"Success at the next level isn't just about how you play; it's about how you're prepared. We integrated IXL because it was the engine that powered our 4.0 standard. I guarantee that if you follow this academic logic, your 'Officially Cleared' status will never be in question."`,
  executive: `"When you enter The Scholar Collective, you are moving from a sports parent to a Household Executive. I am personally committed to ensuring you have the strategy, the data, and the discipline to manage your athlete's legacy at the highest level."`
};

const MeetTheDirectorModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative bg-gray-900 rounded-2xl max-w-lg w-full border border-gray-800 overflow-hidden">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="p-6 text-center border-b border-gray-800">
          <div className="flex items-center justify-center gap-3">
            <User className="w-6 h-6 text-white" />
            <h2 className="text-xl font-black text-white">MEET THE DIRECTOR</h2>
          </div>
        </div>

        {/* Video */}
        <div className="p-4">
          <div className="relative rounded-xl overflow-hidden border-2 border-orange-500/30">
            <iframe
              src="https://www.youtube.com/embed/lZ7ZP3WGmKU"
              title="Meet the Director"
              className="w-full aspect-video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>

        {/* Caption */}
        <div className="px-6 pb-6">
          <p className="text-gray-300 text-sm leading-relaxed">
            <span className="text-orange-500 font-bold">Director Brandon Williamson</span> and his family are the architects of the Scholar-Athlete blueprint. Featured by IXL for elite academic and athletic integration, the Director ensures every Collective household has the roadmap to professional excellence.
          </p>
        </div>

        {/* Close Button */}
        <div className="px-6 pb-6">
          <Button
            onClick={onClose}
            className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold py-4"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

// Reusable Button + Blurb component
const MeetTheDirector = ({ 
  variant = 'blueprint', // 'blueprint' | 'performance' | 'executive'
  isParent = false,
  showBlurb = true,
  className = ''
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const blurb = GUARANTEE_BLURBS[variant];
  const themeColor = isParent ? 'purple' : 'orange';

  return (
    <>
      <div className={`${className}`}>
        {/* Guarantee Blurb */}
        {showBlurb && (
          <div className="mb-4">
            <p className={`text-gray-400 text-sm italic leading-relaxed`}>
              {blurb}
            </p>
            {/* Digital Signature */}
            <div className={`flex items-center gap-2 mt-2 ${isParent ? 'text-purple-500' : 'text-orange-500'}`}>
              <PenLine className="w-4 h-4" />
              <span className="text-xs font-bold tracking-wider">— DIRECTOR B. WILLIAMSON</span>
            </div>
          </div>
        )}

        {/* Meet the Director Button */}
        <Button
          onClick={() => setIsModalOpen(true)}
          className={`${isParent 
            ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50 hover:bg-purple-500/30' 
            : 'bg-orange-500/20 text-orange-500 border border-orange-500/50 hover:bg-orange-500/30'
          } font-bold transition-all`}
        >
          <User className="w-4 h-4 mr-2" />
          MEET THE DIRECTOR
        </Button>
      </div>

      {/* Modal */}
      <MeetTheDirectorModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </>
  );
};

export default MeetTheDirector;
export { MeetTheDirectorModal, GUARANTEE_BLURBS };
